/**
 * W37 (C8; engine FR-E58, plan D170; ADR-0025) — a PDF that held many documents, as the
 * service answers it.
 *
 * - On a **document**: `split_state` — an original's `split` (cut into parts, kept
 *   unchanged) or `uncertain` (the boundaries did not clear the threshold, so it was read
 *   whole for a person to decide); a **part**'s `split_from {document_id, version}` and
 *   `split_pages {from, to, of}`; and, on the single read of a split original, its
 *   `split_parts` — each part's id, status and pages.
 * - On a **job** of kind `split`, once completed: `split` — the boundaries the engine
 *   found (after which page, the score, the evidence), its doubts, and the parts it made.
 *
 * Plain parsers, no React: the register, the document view and a host's own screen read
 * them the same way.
 */

/** An original's mark: cut into parts, or read whole because the engine was not sure. */
export type SplitState = 'split' | 'uncertain'

/** A part's pages in its original, 1-based: `from` to `to` of `of`. */
export interface SplitPages {
  from: number
  to: number
  of: number
}

/** A part's original: the document and the version it was cut from. */
export interface SplitFrom {
  document_id: string
  version: number
}

/** One part of a split original, on the original's single read. */
export interface SplitPart {
  id: string
  status: string
  pages: SplitPages
}

/** One detector's word on a boundary — its reason may quote the page (a number, a GSTIN). */
export interface SplitEvidence {
  detector: string
  points: number
  reason: string
}

/** A boundary: a new document starts after `after_page`. */
export interface SplitBoundary {
  after_page: number
  score: number
  evidence: SplitEvidence[]
}

/** A completed split job's answer (`GET /jobs/{id}` → `split`). */
export interface SplitAnswer {
  pages: number
  threshold: number
  /** Where the file was cut — or, when `split_uncertain`, where it would have been. */
  boundaries: SplitBoundary[]
  /** The pages the evidence left in doubt: nothing is cut while there is one. */
  uncertain: SplitBoundary[]
  split_uncertain: boolean
  ocr_pages: number[]
  /** The documents made, in page order — empty when nothing was cut. */
  parts: { document_id: string; from: number; to: number; of: number }[]
}

const record = (raw: unknown): Record<string, unknown> | null =>
  raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null
const int = (raw: unknown): number | null => (typeof raw === 'number' && Number.isInteger(raw) ? raw : null)
const text = (raw: unknown): string | null => (typeof raw === 'string' ? raw : null)

/** `split_state` as the service sent it — `null` when absent or unknown. */
export function splitStateOf(raw: unknown): SplitState | null {
  return raw === 'split' || raw === 'uncertain' ? raw : null
}

/** `{from, to, of}`, or `null` when it does not hold together. */
export function splitPagesOf(raw: unknown): SplitPages | null {
  const r = record(raw)
  const from = int(r?.from)
  const to = int(r?.to)
  const of = int(r?.of)
  if (from === null || to === null || of === null || from < 1 || to < from || of < to) return null
  return { from, to, of }
}

export function splitFromOf(raw: unknown): SplitFrom | null {
  const r = record(raw)
  const documentId = text(r?.document_id)
  const version = int(r?.version)
  return documentId && version !== null ? { document_id: documentId, version } : null
}

export function splitPartsOf(raw: unknown): SplitPart[] {
  if (!Array.isArray(raw)) return []
  const parts: SplitPart[] = []
  for (const item of raw) {
    const r = record(item)
    const pages = splitPagesOf(r?.pages)
    const id = text(r?.id)
    if (id && pages) parts.push({ id, status: text(r?.status) ?? 'stored', pages })
  }
  return parts.sort((a, b) => a.pages.from - b.pages.from)
}

function boundariesOf(raw: unknown): SplitBoundary[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const r = record(item)
    const after = int(r?.after_page)
    if (after === null) return []
    const evidence = Array.isArray(r?.evidence)
      ? (r.evidence as unknown[]).flatMap((e) => {
          const x = record(e)
          const detector = text(x?.detector)
          return detector ? [{ detector, points: int(x?.points) ?? 0, reason: text(x?.reason) ?? '' }] : []
        })
      : []
    return [{ after_page: after, score: int(r?.score) ?? 0, evidence }]
  })
}

/** A split job's `split` block, or `null` when there is none to read. */
export function splitAnswerOf(raw: unknown): SplitAnswer | null {
  const r = record(raw)
  if (!r) return null
  const parts = Array.isArray(r.parts)
    ? (r.parts as unknown[]).flatMap((p) => {
        const x = record(p)
        const pages = splitPagesOf(x)
        const documentId = text(x?.document_id)
        return documentId && pages ? [{ document_id: documentId, ...pages }] : []
      })
    : []
  return {
    pages: int(r.pages) ?? 0,
    threshold: int(r.threshold) ?? 60,
    boundaries: boundariesOf(r.boundaries),
    uncertain: boundariesOf(r.uncertain),
    split_uncertain: r.split_uncertain === true,
    ocr_pages: Array.isArray(r.ocr_pages) ? (r.ocr_pages as unknown[]).flatMap((n) => (int(n) === null ? [] : [n as number])) : [],
    parts,
  }
}

/** *pages 3 – 4 of 6*, or *page 3 of 6* for a one-page part. */
export function pagesLabel(pages: SplitPages): string {
  return pages.from === pages.to ? `page ${pages.from} of ${pages.of}` : `pages ${pages.from} – ${pages.to} of ${pages.of}`
}

/** The register's short word for a split document — a part's pages, an original's mark — or `null`. */
export function splitMarkOf(doc: { split_state?: string | null; split_pages?: unknown }): string | null {
  const pages = splitPagesOf(doc.split_pages)
  if (pages) return pages.from === pages.to ? `p. ${pages.from} of ${pages.of}` : `pp. ${pages.from}–${pages.to} of ${pages.of}`
  const state = splitStateOf(doc.split_state)
  if (state === 'split') return 'Split'
  if (state === 'uncertain') return 'Split uncertain'
  return null
}
